import { G } from '@mobily/ts-belt';

import { shouldPreferEmployeeByEmploymentStatus } from '@Common/bo/prefer_employee_by_employment_status';
import type { EmployeeStatus } from '@Common/types/bo/b2b/employees_properties';

type EmployeeWithIdentifier = {
  id: number;
  status: EmployeeStatus;
  nir?: string | null;
  ntt?: string | null;
  is_user_closed_or_archived?: boolean;
};

/**
 * When several employees share the same NIR/NTT (e.g. rehire: past + current, or a closed
 * duplicate next to the still-valid employee), keep the preferred one: not closed/archived >
 * closed/archived, then current > retired > past. Same status → higher id (newer row).
 */
export function pickPreferredEmployeeByIdentifier<T extends EmployeeWithIdentifier>(
  employees: T[],
): Map<string, T> {
  const byIdentifier = new Map<string, T>();

  for (const employee of employees) {
    const identifier = employee.nir ?? employee.ntt;
    if (G.isNullable(identifier) || identifier === '') {
      continue;
    }

    const existing = byIdentifier.get(identifier);
    if (
      G.isNullable(existing) ||
      shouldPreferEmployeeByEmploymentStatus(
        { ...employee, isClosedOrArchived: employee.is_user_closed_or_archived },
        { ...existing, isClosedOrArchived: existing.is_user_closed_or_archived },
      )
    ) {
      byIdentifier.set(identifier, employee);
    }
  }

  return byIdentifier;
}

export function keepPreferredEmployeesByIdentifier<T extends EmployeeWithIdentifier>(
  employees: T[],
): T[] {
  const preferred = pickPreferredEmployeeByIdentifier(employees);
  return employees.filter((employee) => {
    const identifier = employee.nir ?? employee.ntt;
    if (G.isNullable(identifier) || identifier === '') {
      return true;
    }
    return preferred.get(identifier)?.id === employee.id;
  });
}
